import { atom, atomFamily, selector, selectorFamily } from 'recoil'
import type { AtomEffect, GetRecoilValue } from 'recoil'
import type { Locale } from '@messenger/types'
import i18n from '@/i18n'
import formatDate from '@/utils/dates/formatDate'
import {
  editorPageSetIsTurnedOver,
  editorRoleDecorationInputs,
  editorTextBoxViewModelResults,
  isEditorTextBoxItemType,
  pageSetContentLocale,
  resolveEditorTextBoxDocument,
  toEditorTextBoxCaseData,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorPageContentBundle,
  EditorRoleDecorationInputs,
  EditorTextBoxDocument,
  EditorTextBoxResolveInputs,
  EditorTextBoxViewModelResult,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { caseAtom } from './caseAtoms'
import { itemsLockedAtom } from './editorAtoms'
import { caseFuneralHomeAtom } from './globalAtoms'
import { selectedThemeAtom } from './printPackageAtoms'
import { dateFormatAtom } from './settingsAtoms'
import {
  canvasPageIdAtom,
  editorPagesAtom,
  itemAtomFamily,
  itemIdAtom,
  pageSetLocaleAtom,
} from './_editorAtoms'

/**
 * Whether the open page set is turned over to Lotus text boxes: any of its
 * pages carries a turnover run id. A selector, so a reader re-renders only
 * when the answer changes, not on every page edit.
 */
export const editorPageSetTurnedOverSelector = selector<boolean>({
  key: 'editorPageSetTurnedOver',
  get: ({ get }) => editorPageSetIsTurnedOver(get(editorPagesAtom)),
})

export const EDITOR_LOTUS_ENABLED_STORAGE_KEY = 'EDITOR_LOTUS_TEXT_BOXES'

// The staff switch starts on: a browser that never stored it reads it as on,
// and only a stored 'false' reads as off.
const persistEditorLotusEnabled: AtomEffect<boolean> = ({ setSelf, onSet }) => {
  try {
    setSelf(
      window.localStorage.getItem(EDITOR_LOTUS_ENABLED_STORAGE_KEY) !== 'false',
    )
  } catch {
    // Storage unavailable — the setting keeps its default, on.
  }
  onSet((value) => {
    try {
      window.localStorage.setItem(
        EDITOR_LOTUS_ENABLED_STORAGE_KEY,
        value ? 'true' : 'false',
      )
    } catch {
      // The change applies for this visit; it just does not persist.
    }
  })
}

export const editorLotusEnabledAtom = atom<boolean>({
  key: 'editorLotusEnabled',
  default: true,
  effects_UNSTABLE: [persistEditorLotusEnabled],
})

/**
 * What the editor load read about Lotus for the open order's funeral home:
 * whether Lotus text boxes are offered there. Null until that read answers,
 * and after it fails.
 */
export const editorLotusFuneralHomeAccessAtom = atom<{
  funeralHomeId: string
  allowed: boolean
} | null>({
  key: 'editorLotusFuneralHomeAccess',
  default: null,
})

/**
 * The frames of the Lotus text boxes the canvas holds, and its page, as one
 * string that changes only when one of those boxes appears, goes, moves,
 * resizes, turns, locks or hides. A move reaches a box's draft only when a
 * Save folds the canvas's geometry in (`useEditorFlushTextBoxGeometry`), and a
 * pasted box reaches the page JSON only at a Save, so a reader that judges the
 * boxes where the canvas shows them re-reads when this changes.
 */
export const editorCanvasTextBoxFramesSelector = selector<string>({
  key: 'editorCanvasTextBoxFrames',
  get: ({ get }) =>
    JSON.stringify([
      get(canvasPageIdAtom),
      get(itemIdAtom)
        .map((id) => get(itemAtomFamily(id)))
        .filter((item) => isEditorTextBoxItemType(item.type))
        .map((item) => [
          item.id,
          item.x,
          item.y,
          item.width,
          item.height,
          item.z,
          item.edits?.rotate ?? 0,
          item.assetLocked ?? false,
          item.hideInPdf ?? false,
        ]),
    ]),
})

export const editorPageContentAtomFamily = atomFamily<
  EditorPageContentBundle | null,
  string
>({
  key: 'editorPageContent',
  default: null,
})

const liveEditorTextBoxResolution = (get: GetRecoilValue) => {
  const caseDetails = get(caseAtom) as Record<string, unknown> | null
  const funeralHomeDetails = get(caseFuneralHomeAtom) as Record<
    string,
    unknown
  > | null
  const theme = get(selectedThemeAtom) as { application?: string } | null
  const dateFormat = get(dateFormatAtom) as string
  const pageSetLocale = get(pageSetLocaleAtom) as Locale
  const caseData = toEditorTextBoxCaseData(caseDetails, {
    dateFormat,
    locale: pageSetLocale,
    formatDate,
    funeralHomeDetails,
    themeApplication: theme?.application,
    translate: (key, values) => i18n.t(key, { ...values, lng: pageSetLocale }),
  })
  return { caseData, locale: pageSetContentLocale(pageSetLocale) }
}

/**
 * The live inputs every page's boxes resolve against — the case, the home,
 * the theme, the date format and the locale — for a reader that holds a
 * bundle of its own rather than the one in `editorPageContentAtomFamily`.
 */
export const editorTextBoxResolveInputsSelector =
  selector<EditorTextBoxResolveInputs>({
    key: 'editorTextBoxResolveInputs',
    get: ({ get }) => liveEditorTextBoxResolution(get),
  })

export const editorTextBoxDocumentsSelector = selectorFamily<
  Map<string, EditorTextBoxViewModelResult> | null,
  string
>({
  key: 'editorTextBoxDocuments',
  get:
    (pageId) =>
    ({ get }) => {
      const bundle = get(editorPageContentAtomFamily(pageId))
      if (bundle === null) return null
      return editorTextBoxViewModelResults(
        bundle,
        get(editorTextBoxResolveInputsSelector),
      )
    },
})

export const editorRoleDecorationsSelector = selectorFamily<
  EditorRoleDecorationInputs,
  string
>({
  key: 'editorRoleDecorations',
  get:
    (pageId) =>
    ({ get }) => {
      const bundle = get(editorPageContentAtomFamily(pageId))
      return bundle === null ? {} : editorRoleDecorationInputs(bundle)
    },
})

export const editorTextBoxDraftsAtom = atom<
  Record<string, EditorTextBoxDocument>
>({
  key: 'editorTextBoxDrafts',
  default: {},
})

type ResolvedDraftCacheEntry = {
  draft: EditorTextBoxDocument
  resolutionSignature: string
  resolved: EditorTextBoxDocument
}

const resolvedDraftCache = new Map<string, ResolvedDraftCacheEntry>()

const resolveDraftAgainstLiveCase = (
  draft: EditorTextBoxDocument,
  resolution: ReturnType<typeof liveEditorTextBoxResolution>,
): EditorTextBoxDocument => {
  try {
    return resolveEditorTextBoxDocument({
      anchor: draft.anchor,
      revision: draft.revision,
      box: draft.box,
      context: { ...draft.context, ...resolution },
    })
  } catch {
    // A draft that cannot re-resolve paints as committed rather than
    // taking the canvas down with it.
    return draft
  }
}

export const editorTextBoxDraftSelector = selectorFamily<
  EditorTextBoxDocument | null,
  string
>({
  key: 'editorTextBoxDraft',
  get:
    (boxId) =>
    ({ get }) => {
      const draft = get(editorTextBoxDraftsAtom)[boxId] ?? null
      if (draft === null) {
        resolvedDraftCache.delete(boxId)
        return null
      }
      const resolution = liveEditorTextBoxResolution(get)
      const resolutionSignature = JSON.stringify(resolution)
      const cached = resolvedDraftCache.get(boxId)
      if (
        cached !== undefined &&
        cached.draft === draft &&
        cached.resolutionSignature === resolutionSignature
      )
        return cached.resolved
      const resolved = resolveDraftAgainstLiveCase(draft, resolution)
      resolvedDraftCache.set(boxId, { draft, resolutionSignature, resolved })
      return resolved
    },
})

/**
 * Box id → the id of the page whose rows hold it, for boxes deleted in this
 * session but not yet saved. The save command only deletes boxes named in its
 * payload, so the id has to survive until the save lands; page hydration skips
 * these ids so the rows cannot resurrect a deleted box.
 */
export const editorTextBoxPendingDeletesAtom = atom<Record<string, string>>({
  key: 'editorTextBoxPendingDeletes',
  default: {},
})

/**
 * The ids of the pages this tab is deleting itself, held from the moment the
 * shuffler decides to delete one. A save already in flight for such a page
 * fails on the row the delete removed and comes back with the same
 * page-is-gone answer a layout change made in another tab produces, and only
 * this set tells the two apart: a page in it is the user's own delete doing
 * what they asked, not a reason to tell them the layout changed elsewhere.
 * The editor's exit reset clears it; a recreated page never reuses an id.
 */
export const editorSelfDeletedPageIdsAtom = atom<Record<string, true>>({
  key: 'editorSelfDeletedPageIds',
  default: {},
})

/**
 * The ids of the boxes this session inserted from the toolbar
 * (`useEditorInsertTextBox`) whose first paint has not yet run. A fresh box
 * is drawn at the seed height, shorter than its one line of text, and its
 * first paint raises the frame to the text (`textBoxSeedFrameHeightPx`).
 * Nothing else is a seed: a saved row opens at its saved frame however
 * short, on every surface. An id leaves the set with that first paint, and
 * the editor's exit reset clears the rest.
 */
export const editorTextBoxSeedIdsAtom = atom<Record<string, true>>({
  key: 'editorTextBoxSeedIds',
  default: {},
})

/**
 * The ids the editor load minted for page items stored without an id, or
 * with an id an earlier page already held (`deCollideEditorItemIds`). The
 * stored page JSON still names such an item by its old id, so no text-box
 * row may be written under the new one: a conversion refuses these ids.
 * A Save that writes every page persists them and clears the set; the
 * editor's exit reset clears it too.
 */
export const editorRemintedItemIdsAtom = atom<Record<string, true>>({
  key: 'editorRemintedItemIds',
  default: {},
})

/**
 * Whether the overlay's lock icon flips an editor text box's own lock. Off:
 * the icon only shows the lock, and a box follows the page's lock setting
 * like every other item. The flip, the session map and the selectors below
 * stay wired so turning this back on is this one line.
 */
export const EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR = false

/**
 * Box id → lock, for every box whose lock this session set through the lock
 * icon's flip (while `EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR` is on). A box in
 * this map keeps that lock while the page is open, apart from the page's
 * lock setting; a box not in it follows the setting. Nothing is saved: a
 * flip lives until the editor's exit reset clears the map.
 */
export const editorTextBoxLocksAtom = atom<Record<string, boolean>>({
  key: 'editorTextBoxLocks',
  default: {},
})

/**
 * The lock a box has when nothing set one: the page's lock setting, which
 * the editor opens off for an admin and on for every other user, and the
 * settings toggle flips.
 */
export const editorTextBoxDefaultLockedSelector = selector<boolean>({
  key: 'editorTextBoxDefaultLocked',
  get: ({ get }) => get(itemsLockedAtom),
})

/**
 * A box's lock: this session's value, else the page's lock setting. Locked
 * forbids the handles, the move control and the nudge; typing, styling and
 * growth never consult it.
 */
export const editorTextBoxLockedSelector = selectorFamily<boolean, string>({
  key: 'editorTextBoxLocked',
  get:
    (boxId) =>
    ({ get }) => {
      const session = get(editorTextBoxLocksAtom)[boxId]
      if (typeof session === 'boolean') return session
      return get(editorTextBoxDefaultLockedSelector)
    },
})

/**
 * The lock that governs an item on the canvas: an editor text box's own lock
 * (the page's lock setting unless this session set one), the page's lock
 * setting for every other item. The overlay chrome with its handles, the
 * functional handles in `ResizeControls.tsx`, the drawer's width and height
 * fields, and Item.js's drag and nudge read this one selector.
 */
export const editorItemLockedSelector = selectorFamily<boolean, string>({
  key: 'editorItemLocked',
  get:
    (itemId) =>
    ({ get }) =>
      isEditorTextBoxItemType(get(itemAtomFamily(itemId))?.type)
        ? get(editorTextBoxLockedSelector(itemId))
        : get(itemsLockedAtom),
})
